import { PlusChip } from './Plus';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, Clock, Heart, Plus } from 'lucide-react';
import { RecipeImage } from './RecipeImage';
import type { KitchenMode, Recommendation } from '../domain/recommend';
import type { RankedRecipe } from '../domain/ranking';
import { displayName } from '../domain/taxonomy';
import { joinNatural, capitalize } from '../domain/text';
import { estimateMeal, macroLine } from '../domain/nutrition/estimate';
import { useStore } from '../state/store';

const minutes = (m: number) => `${m} min`;

/**
 * Short kitchen status for a card: "Ready to cook", "Need 2", or nothing when the user hasn't told
 * us what they have (a guess would be presented as fact).
 */
export function kitchenNote(r: RankedRecipe & { kitchenMode?: KitchenMode }, detail = false): string | null {
  if (r.kitchenMode === 'unknown') return null;
  if (r.kitchenMode === 'ignored') return `Buy ${r.missing.length}`;
  if (r.makeable) return 'Ready to cook';
  if (!detail) return `Need ${r.missing.length}`;
  return `Need ${joinNatural(r.missing.slice(0, 2).map(displayName))}${r.missing.length > 2 ? ` +${r.missing.length - 2}` : ''}`;
}
const withNote = (head: string, note: string | null) => (note ? `${head} · ${note}` : head);

/**
 * Calories, carbs and protein per serving, as cooked. Part of Amari Plus: free accounts see a
 * "with Plus" marker instead. Hidden when the user turned numbers off.
 */
export function Macros({ r, className = 'block text-footnote text-fg-muted mt-0.5' }: { r: RankedRecipe; className?: string }) {
  const hide = useStore((s) => s.profile.hideNumbers);
  const plus = useStore((s) => s.membership === 'plus');
  const e = hide ? null : estimateMeal(r);
  if (!e) return null;
  if (!plus) return <span className={className} data-testid="macros-locked"><PlusChip>Calories &amp; protein with Plus</PlusChip></span>;
  return <span className={className} data-testid="macros">{macroLine(e)} per serving</span>;
}

/** Portrait, story-style preview for the "Not feeling it?" rail. Tapping makes it tonight's pick. */
export function StoryCard({ r, onSelect }: { r: Recommendation; onSelect: () => void }) {
  return (
    <button onClick={onSelect} className="card-interactive relative block w-[44vw] min-w-[150px] max-w-[190px] text-left rounded-[18px] overflow-hidden"
      aria-label={`Make ${r.recipe.title} tonight instead. ${r.recipe.minutes} minutes.${r.reasons[0] ? ` ${r.reasons[0]}.` : ''}`}>
      <RecipeImage recipeId={r.recipe.id} ratio="2/3" rounded="rounded-[18px]" />
      <span className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/80 via-black/45 to-transparent" aria-hidden />
      <span className="absolute inset-x-0 bottom-0 p-3 text-white" aria-hidden>
        <span className="block text-headline leading-tight line-clamp-2 [text-shadow:0_1px_2px_rgb(0_0_0/0.4)]">{r.recipe.title}</span>
        <span className="flex items-center gap-1 text-footnote mt-1 text-white/90"><Clock size={12} />{minutes(r.recipe.minutes)}</span>
        <Macros r={r} className="block text-footnote text-white/90 mt-0.5" />
        {r.reasons[0] && <span className="block text-footnote text-white/85 mt-0.5 line-clamp-2">{r.reasons[0]}</span>}
      </span>
    </button>
  );
}

/** One or two ingredients away: shows what you have and what to buy, with an add-to-list action. */
export function AlmostCard({ r, onAdd, onList }: { r: RankedRecipe; onAdd: () => void; onList: boolean }) {
  const have = r.usesFromKitchen.slice(0, 3).map(displayName);
  const need = r.missing.map(displayName);
  return (
    <article className="w-[76vw] min-w-[250px] max-w-[300px]">
      <Link to={`/recipe/${r.recipe.id}`} className="block card-interactive" aria-label={`${r.recipe.title}, ${r.recipe.minutes} minutes. Need ${joinNatural(need)}.`}>
        <RecipeImage recipeId={r.recipe.id} ratio="4/3" rounded="rounded-[18px]" />
        <h3 className="text-headline mt-3 line-clamp-1">{r.recipe.title}</h3>
      </Link>
      <p className="text-subhead text-fg-muted flex items-center gap-1 mt-0.5"><Clock size={13} aria-hidden />{minutes(r.recipe.minutes)}</p>
      <Macros r={r} />
      <dl className="mt-2.5 grid gap-1.5 text-subhead">
        <div className="flex gap-2 items-baseline">
          <dt className="w-11 shrink-0 text-fg-muted">Need</dt>
          <dd className="flex flex-wrap gap-1.5">{need.map((n) => <span key={n} className="tag tag-need">{capitalize(n)}</span>)}</dd>
        </div>
        {have.length > 0 && (
          <div className="flex gap-2 items-baseline">
            <dt className="w-11 shrink-0 text-fg-muted">Have</dt>
            <dd className="text-fg line-clamp-1 first-letter:uppercase">{joinNatural(have)}{r.usesFromKitchen.length > 3 ? ` +${r.usesFromKitchen.length - 3}` : ''}</dd>
          </div>
        )}
      </dl>
      <button className="btn btn-sm btn-secondary w-full mt-3" onClick={onAdd} disabled={onList}>
        {onList ? <><Check size={16} aria-hidden /> On your list</> : <><Plus size={16} aria-hidden /> Add {need.length === 1 ? need[0] : `${need.length} items`} to list</>}
      </button>
    </article>
  );
}

/** Selectable card for the weekly plan's 10 suggestions. */
export function CandidateCard({ r, selected, onToggle, disabled }: { r: Recommendation; selected: boolean; onToggle: () => void; disabled: boolean }) {
  return (
    <div className="relative">
      <button onClick={onToggle} aria-pressed={selected} disabled={disabled && !selected}
        className={`card-interactive block w-full text-left disabled:opacity-45 ${selected ? '' : ''}`}
        aria-label={`${r.recipe.title}, ${withNote(minutes(r.recipe.minutes), kitchenNote(r))}`}>
        <div className={`relative rounded-[16px] transition-shadow duration-200 ${selected ? 'ring-[3px] ring-primary ring-offset-2 ring-offset-bg' : ''}`}>
          <RecipeImage recipeId={r.recipe.id} ratio="4/5" rounded="rounded-[16px]" />
          <span className={`absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center transition-colors ${selected ? 'bg-primary text-primary-on' : 'bg-black/35 text-white border-2 border-white/90'}`} aria-hidden>
            {selected && <Check size={16} strokeWidth={3} />}
          </span>
        </div>
        <span className="block text-headline mt-2 leading-snug line-clamp-2">{r.recipe.title}</span>
        <span className="block text-footnote text-fg-muted mt-0.5">
          {withNote(minutes(r.recipe.minutes), kitchenNote(r))}
        </span>
        <Macros r={r} />
        {r.reasons[0] && <span className="block text-footnote text-fg-muted mt-0.5 line-clamp-2">{r.reasons[0]}</span>}
      </button>
    </div>
  );
}

/** Compact list row with a thumbnail. */
export function MealRow({ r, to, trailing, meta }: { r: RankedRecipe & { kitchenMode?: KitchenMode }; to?: string; trailing?: React.ReactNode; meta?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 min-h-[72px]">
      <Link to={to ?? `/recipe/${r.recipe.id}`} className="flex items-center gap-3 flex-1 min-w-0 py-2">
        <RecipeImage recipeId={r.recipe.id} ratio="1/1" rounded="rounded-[12px]" className="w-14 shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="block text-headline leading-tight truncate">{r.recipe.title}</span>
          <span className="block text-subhead text-fg-muted mt-0.5 truncate">
            {meta ?? withNote(minutes(r.recipe.minutes), kitchenNote(r, true))}
          </span>
          <Macros r={r} className="block text-footnote text-fg-muted mt-0.5" />
        </span>
        {!trailing && <ChevronRight size={18} className="text-fg-muted/60 shrink-0" aria-hidden />}
      </Link>
      {trailing}
    </div>
  );
}

/** Backwards-compatible name used by older screens. */
export const RecipeRow = MealRow;

/** Save or unsave a recipe; saving needs a free account (asked only at that moment). */
export function SaveHeart({ recipeId, className = '' }: { recipeId: string; className?: string }) {
  const saved = useStore((s) => s.saved.includes(recipeId));
  const toggleSaved = useStore((s) => s.toggleSaved);
  const requireAccount = useStore((s) => s.requireAccount);
  const showToast = useStore((s) => s.showToast);
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (saved) { toggleSaved(recipeId); showToast('Removed from favorites'); return; }
    requireAccount('save recipes', () => { toggleSaved(recipeId); showToast('Saved to favorites'); });
  };
  return (
    <button type="button" onClick={onClick} aria-pressed={saved} aria-label={saved ? 'Remove from favorites' : 'Save to favorites'}
      className={`w-9 h-9 rounded-full flex items-center justify-center btn-on-image active:scale-90 transition-transform ${className}`}>
      <Heart size={19} strokeWidth={2.2} fill={saved ? 'currentColor' : 'none'} aria-hidden />
    </button>
  );
}

/** A recipe card for the home rails: photo, time, save, title, one line of why. */
export function MealCard({ r, wide = false }: { r: Recommendation; wide?: boolean }) {
  const why = r.reasons[0] ?? kitchenNote(r, true);
  return (
    <Link to={`/recipe/${r.recipe.id}`} className={`tile tile-interactive block overflow-hidden ${wide ? 'w-full' : 'w-[62vw] min-w-[200px] max-w-[240px]'}`}
      aria-label={`${r.recipe.title}, ${r.recipe.minutes} minutes`}>
      <span className="relative block">
        <RecipeImage recipeId={r.recipe.id} ratio="4/3" rounded="rounded-none" />
        <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-full btn-on-image px-2.5 py-1 text-[13px] font-semibold">
          <Clock size={13} aria-hidden /> {r.recipe.minutes} min
        </span>
        <SaveHeart recipeId={r.recipe.id} className="absolute top-2 right-2" />
      </span>
      <span className="block p-3.5">
        <span className="block text-headline leading-snug line-clamp-2 min-h-[44px]">{r.recipe.title}</span>
        {why && <span className="block text-footnote text-fg-muted mt-1 line-clamp-2">{capitalize(why)}</span>}
        <Macros r={r} className="block text-caption text-fg-muted mt-1.5" />
      </span>
    </Link>
  );
}

/** "Almost there": what's missing, and one tap to put it on the list. */
export function MissingRow({ r, onAdd, onList }: { r: RankedRecipe; onAdd: () => void; onList: boolean }) {
  return (
    <div className="tile flex items-center gap-3 p-2.5 pr-3">
      <Link to={`/recipe/${r.recipe.id}`} className="flex items-center gap-3 flex-1 min-w-0">
        <RecipeImage recipeId={r.recipe.id} ratio="1/1" rounded="rounded-[14px]" className="w-[84px] shrink-0" />
        <span className="flex-1 min-w-0">
          <span className="block text-headline leading-snug line-clamp-2">{r.recipe.title}</span>
          <span className="block text-footnote text-fg-muted mt-0.5 mb-1">You&rsquo;re missing:</span>
          <span className="flex flex-wrap gap-1">
            {r.missing.slice(0, 3).map((m) => <span key={m} className="tag tag-warn">{capitalize(displayName(m))}</span>)}
          </span>
        </span>
      </Link>
      <button type="button" onClick={onAdd} disabled={onList}
        aria-label={onList ? `${r.recipe.title}: already on your list` : `Add ${joinNatural(r.missing.map(displayName))} to your list`}
        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-warning-bg text-warning-fg active:scale-90 transition-transform disabled:opacity-100">
        {onList ? <Check size={20} aria-hidden /> : <Plus size={22} aria-hidden />}
      </button>
    </div>
  );
}
